import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Search } from '@mui/icons-material';
import {
    saveShowSubscribeStatus,
    setSpeakerSubscribeStatus,
} from '../redux/actions/userActions';
import { getSubscribeStatus } from '../utils/subscribeStatus';
import { SpeakerPreferenceRow, ShowPreferenceRow } from './SettingsPreferenceComponents';
import FullWidthButton from './FullWidthButton';
import './LibrarySuggestions.css';

const NOOP = () => {};
const SUGGESTION_LIMIT = 5;

const useTrendingSuggestions = (endpoint, field, statusById, subscribeIds) => {
    const [state, setState] = useState({ items: [], loading: true, error: false });
    const requestRef = useRef(0);
    const statusRef = useRef({ statusById, subscribeIds });
    statusRef.current = { statusById, subscribeIds };

    const load = useCallback(async () => {
        const request = ++requestRef.current;
        setState(current => ({ ...current, loading: true, error: false }));
        try {
            const { data } = await axios.get(endpoint);
            if (data?.error || (data?.[field] !== undefined && !Array.isArray(data[field]))) {
                throw new Error('Unable to load suggestions.');
            }
            const seen = new Set();
            const items = (data?.[field] || []).filter(item => {
                if (!item?.id || !item.name || seen.has(item.id)) return false;
                const { statusById: currentStatuses, subscribeIds: currentIds } = statusRef.current;
                if (getSubscribeStatus(currentStatuses, currentIds, item.id) !== null) return false;
                seen.add(item.id);
                return true;
            }).slice(0, SUGGESTION_LIMIT);
            if (requestRef.current === request) setState({ items, loading: false, error: false });
        } catch {
            if (requestRef.current === request) {
                setState(current => ({ ...current, loading: false, error: true }));
            }
        }
    }, [endpoint, field]);

    useEffect(() => {
        load();
        return () => { requestRef.current += 1; };
    }, [load]);

    return { ...state, retry: load };
};

const SuggestionRow = ({ entity, kind, status, save, onPreferenceSaveStart, onPreferenceSaveComplete }) => {
    const [savingStatus, setSavingStatus] = useState(null);
    const [error, setError] = useState(false);
    const savingRef = useRef(false);
    const mountedRef = useRef(true);
    const saving = savingStatus !== null;
    const displayedStatus = saving ? savingStatus.status : status;

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    const changeStatus = async nextStatus => {
        if (savingRef.current) return;
        savingRef.current = true;
        setSavingStatus({ status: nextStatus });
        setError(false);
        onPreferenceSaveStart();
        let succeeded = false;
        try {
            succeeded = !!(await save(entity, nextStatus));
        } catch {
            // Both a rejected request and a null result use the inline error.
        }
        savingRef.current = false;
        if (!mountedRef.current) return;
        setSavingStatus(null);
        setError(!succeeded);
        onPreferenceSaveComplete(succeeded);
    };

    return (
        <li className='librarySuggestionRow' aria-label={entity.name} aria-busy={saving}>
            {kind === 'speaker' ? <SpeakerPreferenceRow speaker={entity} description={entity.description}
                status={displayedStatus} onStatusChange={changeStatus}
                to={`/speaker/${encodeURIComponent(entity.id)}`}
                controlLabels={{ positiveLabel: `Follow ${entity.name}`, selectedPositiveLabel: `Unfollow ${entity.name}`, negativeLabel: `Not for me: ${entity.name}` }} /> :
                <ShowPreferenceRow source={entity} description={entity.author} status={displayedStatus}
                    onStatusChange={changeStatus} mutation={{ saving }}
                    to={`/podcast/${encodeURIComponent(entity.id)}`}
                    controlLabels={{ positiveLabel: `Follow ${entity.name}`, selectedPositiveLabel: `Unfollow ${entity.name}`, negativeLabel: `Not for me: ${entity.name}` }} />}
            {error && (
                <p className='librarySuggestionSaveError' role='alert'>
                    Couldn't save your preference. Please try again.
                </p>
            )}
        </li>
    );
};

const TrendingSection = ({ title, kind, suggestions, subscribeIds, statusById, ...rowProps }) => (
    <section className='libraryTrendingSection' aria-label={title}>
        <h3 className='libraryTrendingHeading'>{title}</h3>
        {suggestions.loading ? (
            <div className='librarySuggestionLoading' role='status' aria-label={`Loading ${title.toLowerCase()}`}>
                {Array.from({ length: SUGGESTION_LIMIT }, (_, index) => (
                    <div key={index} className='librarySuggestionSkeleton' aria-hidden='true' />
                ))}
            </div>
        ) : suggestions.error ? (
            <div className='librarySuggestionLoadError' role='status'>
                <p>Couldn't load {title.toLowerCase()}.</p>
                <button type='button' onClick={suggestions.retry}>Try again</button>
            </div>
        ) : suggestions.items.length ? (
            <ul className='librarySuggestionList'>
                {suggestions.items.map(entity => (
                    <SuggestionRow
                        key={entity.id}
                        entity={entity}
                        kind={kind}
                        status={getSubscribeStatus(statusById, subscribeIds, entity.id)}
                        {...rowProps}
                    />
                ))}
            </ul>
        ) : (
            <p className='librarySuggestionUnavailable'>No suggestions right now. Try search below.</p>
        )}
    </section>
);

const LibrarySuggestionsContent = ({
    speakerSubscribeIds,
    speakerSubscribeStatusById,
    podcastSubscribeIds,
    podcastSubscribeStatusById,
    setSpeakerSubscribeStatus,
    saveShowSubscribeStatus,
    onPreferenceSaveStart,
    onPreferenceSaveComplete,
}) => {
    const navigate = useNavigate();
    const speakers = useTrendingSuggestions('/top_speakers', 'speakers', speakerSubscribeStatusById, speakerSubscribeIds);
    const shows = useTrendingSuggestions('/top_podcasts', 'sources', podcastSubscribeStatusById, podcastSubscribeIds);

    return (
        <div className='librarySuggestions'>
            <TrendingSection
                title='Trending speakers'
                kind='speaker'
                suggestions={speakers}
                subscribeIds={speakerSubscribeIds}
                statusById={speakerSubscribeStatusById}
                save={(speaker, status) => setSpeakerSubscribeStatus(speaker.id, status, { suppressErrorAlert: true })}
                onPreferenceSaveStart={onPreferenceSaveStart}
                onPreferenceSaveComplete={onPreferenceSaveComplete}
            />
            <TrendingSection
                title='Trending shows'
                kind='show'
                suggestions={shows}
                subscribeIds={podcastSubscribeIds}
                statusById={podcastSubscribeStatusById}
                save={(source, status) => saveShowSubscribeStatus({ source }, status)}
                onPreferenceSaveStart={onPreferenceSaveStart}
                onPreferenceSaveComplete={onPreferenceSaveComplete}
            />
            <FullWidthButton
                Icon={Search}
                title='Search shows & speakers'
                showChevron={false}
                buttonClassName='blurBackground blurPillButton'
                onClick={() => navigate('/search?tab=podcasts')}
            />
        </div>
    );
};

export const LibrarySuggestions = ({
    authenticated,
    uid,
    onPreferenceSaveStart = NOOP,
    onPreferenceSaveComplete = NOOP,
    ...props
}) => authenticated ? (
    <LibrarySuggestionsContent
        key={uid}
        onPreferenceSaveStart={onPreferenceSaveStart}
        onPreferenceSaveComplete={onPreferenceSaveComplete}
        {...props}
    />
) : null;

const mapStateToProps = ({ user }) => ({
    authenticated: user.authenticated,
    uid: user.uid,
    speakerSubscribeIds: user.speakerSubscribeIds,
    speakerSubscribeStatusById: user.speakerSubscribeStatusById,
    podcastSubscribeIds: user.podcastSubscribeIds,
    podcastSubscribeStatusById: user.podcastSubscribeStatusById,
});

export default connect(mapStateToProps, {
    setSpeakerSubscribeStatus,
    saveShowSubscribeStatus,
})(LibrarySuggestions);
